import { z } from 'zod';
import { ProposedActionSchema, type ProposedAction } from './domain';

export const A2UI_PROTOCOL_VERSION = 'v0.9' as const;
export const A2UI_SPEC_COMPATIBILITY = 'v0.9.1' as const;
export const VALO_A2UI_CATALOG_ID = 'https://valoresearch.org/a2ui/catalogs/cockpit/v1' as const;

export const VALO_A2UI_CLIENT_CAPABILITIES = Object.freeze({
  [A2UI_PROTOCOL_VERSION]: {
    supportedCatalogIds: [VALO_A2UI_CATALOG_ID],
    inlineCatalogs: [],
  },
});

export const ValoA2UIComponentNameSchema = z.enum([
  'Text',
  'Card',
  'Row',
  'Column',
  'Divider',
  'Button',
  'AuthorityCard',
  'EvidenceCard',
  'StandingCard',
  'ActionIntent',
  'StepUpApproval',
  'DecisionReceipt',
  'ExecutionStatus',
  'HumanJudgment',
  'WorldStateDiff',
]);
export type ValoA2UIComponentName = z.infer<typeof ValoA2UIComponentNameSchema>;

const DynamicValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  z.object({ path: z.string().startsWith('/') }).strict(),
]);

const A2UIEventActionSchema = z.object({
  event: z.object({
    name: z.string().min(1),
    context: z.record(z.string(), z.unknown()).optional(),
  }).strict(),
}).strict();

export const ValoA2UIComponentSchema = z.object({
  id: z.string().min(1),
  component: ValoA2UIComponentNameSchema,
  children: z.array(z.string().min(1)).optional(),
  child: z.string().min(1).optional(),
  text: DynamicValueSchema.optional(),
  title: DynamicValueSchema.optional(),
  body: DynamicValueSchema.optional(),
  status: DynamicValueSchema.optional(),
  label: DynamicValueSchema.optional(),
  variant: z.string().optional(),
  action: A2UIEventActionSchema.optional(),
}).strict();
export type ValoA2UIComponent = z.infer<typeof ValoA2UIComponentSchema>;

const CreateSurfaceSchema = z.object({
  version: z.literal(A2UI_PROTOCOL_VERSION),
  createSurface: z.object({
    surfaceId: z.string().min(1),
    catalogId: z.literal(VALO_A2UI_CATALOG_ID),
    sendDataModel: z.boolean().optional(),
  }).strict(),
}).strict();

const UpdateComponentsSchema = z.object({
  version: z.literal(A2UI_PROTOCOL_VERSION),
  updateComponents: z.object({
    surfaceId: z.string().min(1),
    components: z.array(ValoA2UIComponentSchema),
  }).strict(),
}).strict();

const UpdateDataModelSchema = z.object({
  version: z.literal(A2UI_PROTOCOL_VERSION),
  updateDataModel: z.object({
    surfaceId: z.string().min(1),
    path: z.string().startsWith('/').optional(),
    value: z.unknown().optional(),
  }).strict(),
}).strict();

const DeleteSurfaceSchema = z.object({
  version: z.literal(A2UI_PROTOCOL_VERSION),
  deleteSurface: z.object({
    surfaceId: z.string().min(1),
  }).strict(),
}).strict();

export const ValoA2UIMessageSchema = z.union([
  CreateSurfaceSchema,
  UpdateComponentsSchema,
  UpdateDataModelSchema,
  DeleteSurfaceSchema,
]);
export type ValoA2UIMessage = z.infer<typeof ValoA2UIMessageSchema>;

export type ValoA2UISurfaceState = {
  surfaceId: string;
  catalogId: typeof VALO_A2UI_CATALOG_ID;
  components: Map<string, ValoA2UIComponent>;
  dataModel: unknown;
  deleted: boolean;
};

export type A2UIClientActionMessage = {
  version: typeof A2UI_PROTOCOL_VERSION;
  action: {
    name: string;
    surfaceId: string;
    sourceComponentId: string;
    timestamp: string;
    context: Record<string, unknown>;
  };
};

export type A2UIActionDispatch = {
  schema: 'valo.cockpit.a2ui.action.v1';
  source: 'A2UI';
  protocolVersion: typeof A2UI_PROTOCOL_VERSION;
  protocolAction: A2UIClientActionMessage;
  surfaceId: string;
  sourceComponentId: string;
  eventName: string;
  requestedAt: string;
  proposedAction: ProposedAction;
  authorizationBoundary: 'REHT';
  executionAuthority: 'NONE';
  receiptRequired: true;
  canAuthorize: false;
  canExecute: false;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function decodePointer(segment: string): string {
  return segment.replace(/~1/g, '/').replace(/~0/g, '~');
}

export function resolveA2UIValue(value: unknown, dataModel: unknown): unknown {
  if (!isRecord(value) || typeof value.path !== 'string') return value;

  return value.path
    .slice(1)
    .split('/')
    .map(decodePointer)
    .reduce<unknown>((current, key) => {
      if (Array.isArray(current)) {
        const index = Number(key);
        return Number.isInteger(index) ? current[index] : undefined;
      }
      return isRecord(current) ? current[key] : undefined;
    }, dataModel);
}

function resolveContext(value: unknown, dataModel: unknown): unknown {
  if (Array.isArray(value)) return value.map((item) => resolveContext(item, dataModel));
  if (!isRecord(value)) return value;
  if (typeof value.path === 'string' && Object.keys(value).length === 1) {
    return resolveA2UIValue(value, dataModel);
  }
  return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, resolveContext(item, dataModel)]));
}

function setDataModelValue(model: unknown, path: string | undefined, value: unknown): unknown {
  if (!path) return value;

  const root: Record<string, unknown> = isRecord(model) ? structuredClone(model) : {};
  const keys = path.slice(1).split('/').map(decodePointer);
  let cursor = root;

  for (const key of keys.slice(0, -1)) {
    const next = cursor[key];
    cursor[key] = isRecord(next) ? next : {};
    cursor = cursor[key] as Record<string, unknown>;
  }

  const leaf = keys.at(-1);
  if (leaf === undefined) return root;
  if (value === undefined) delete cursor[leaf];
  else cursor[leaf] = value;
  return root;
}

function componentReferences(component: ValoA2UIComponent): string[] {
  return [...(component.children ?? []), ...(component.child ? [component.child] : [])];
}

function assertTreeIntegrity(components: Map<string, ValoA2UIComponent>) {
  if (!components.has('root')) throw new Error('A2UI surface requires exactly one root component');

  for (const component of components.values()) {
    for (const reference of componentReferences(component)) {
      if (!components.has(reference)) {
        throw new Error(`A2UI component ${component.id} references missing child ${reference}`);
      }
    }
  }

  const visiting = new Set<string>();
  const visited = new Set<string>();
  const visit = (id: string) => {
    if (visiting.has(id)) throw new Error(`A2UI component graph contains a cycle at ${id}`);
    if (visited.has(id)) return;

    visiting.add(id);
    const component = components.get(id);
    if (component) componentReferences(component).forEach(visit);
    visiting.delete(id);
    visited.add(id);
  };

  visit('root');
}

export function parseValoA2UIStream(input: unknown[]): ValoA2UISurfaceState {
  let state: ValoA2UISurfaceState | undefined;

  for (const raw of input) {
    const message = ValoA2UIMessageSchema.parse(raw);

    if ('createSurface' in message) {
      if (state && !state.deleted) throw new Error('A2UI surface already exists');
      state = {
        surfaceId: message.createSurface.surfaceId,
        catalogId: message.createSurface.catalogId,
        components: new Map(),
        dataModel: {},
        deleted: false,
      };
      continue;
    }

    if (!state) throw new Error('A2UI createSurface must precede updates');
    const surfaceId = 'updateComponents' in message
      ? message.updateComponents.surfaceId
      : 'updateDataModel' in message
        ? message.updateDataModel.surfaceId
        : message.deleteSurface.surfaceId;

    if (surfaceId !== state.surfaceId) throw new Error('A2UI surfaceId mismatch');

    if ('updateComponents' in message) {
      for (const component of message.updateComponents.components) {
        state.components.set(component.id, component);
      }
    } else if ('updateDataModel' in message) {
      state.dataModel = setDataModelValue(state.dataModel, message.updateDataModel.path, message.updateDataModel.value);
    } else {
      state.deleted = true;
    }
  }

  if (!state) throw new Error('A2UI stream is empty');
  if (!state.deleted) assertTreeIntegrity(state.components);
  return state;
}

export function toA2UIClientActionMessage(
  state: ValoA2UISurfaceState,
  componentId: string,
  timestamp = new Date().toISOString(),
): A2UIClientActionMessage {
  if (state.deleted) throw new Error('A2UI surface is deleted');
  const component = state.components.get(componentId);
  if (!component) throw new Error(`A2UI component ${componentId} does not exist`);
  if (!component.action?.event) throw new Error(`A2UI component ${componentId} has no server event action`);

  const resolved = resolveContext(component.action.event.context ?? {}, state.dataModel);
  if (!isRecord(resolved)) throw new Error('A2UI action context must resolve to an object');

  return {
    version: A2UI_PROTOCOL_VERSION,
    action: {
      name: component.action.event.name,
      surfaceId: state.surfaceId,
      sourceComponentId: component.id,
      timestamp,
      context: resolved,
    },
  };
}

export function dispatchA2UIAction(
  state: ValoA2UISurfaceState,
  componentId: string,
  requestedAt = new Date().toISOString(),
): A2UIActionDispatch {
  const protocolAction = toA2UIClientActionMessage(state, componentId, requestedAt);
  if (!('proposedAction' in protocolAction.action.context)) {
    throw new Error('A2UI governed action requires context.proposedAction');
  }

  const proposedAction = ProposedActionSchema.parse(protocolAction.action.context.proposedAction);

  return {
    schema: 'valo.cockpit.a2ui.action.v1',
    source: 'A2UI',
    protocolVersion: A2UI_PROTOCOL_VERSION,
    protocolAction,
    surfaceId: protocolAction.action.surfaceId,
    sourceComponentId: protocolAction.action.sourceComponentId,
    eventName: protocolAction.action.name,
    requestedAt: protocolAction.action.timestamp,
    proposedAction,
    authorizationBoundary: 'REHT',
    executionAuthority: 'NONE',
    receiptRequired: true,
    canAuthorize: false,
    canExecute: false,
  };
}
